import React, { useState, useEffect, useMemo } from 'react';
import { 
    Card, 
    TextInput, 
    TextArea, 
    Select, 
    Option, 
    Button, 
    Variants, 
    Toggle, 
    Label,
    Heading,
    Grid,
    TextTypes
} from '@sede-enterprise/shell-ds-react-framework';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Col } from '../../components/flexbox/Flex';
import { ContainerDiv } from '../../components/styledComponent/StyledComponent';
import TransferList from '../../components/transferList/TransferList';
import Loader from '../../components/loader/Loader';
import { useToast } from 'rc-toastr';
import { usePageTitle } from '../../services/UtilService';
import { useTranslation } from 'react-i18next';
import { getData, postData, putData } from '../../services/ApiService';
import { ApiUrls } from '../../helpers/ApiUrls';
import './styles.css';

interface ReportConfig {
    reportName: string;
    reportDescription: string;
    filterCriteria: string;
    filterCriteriaValue: string[];
    selectedGroups: string[];
    selectedColumns: string[];
    isActive: boolean;
}

interface AuditInfo {
    createdBy?: string;
    createdOn?: string;
    modifiedBy?: string;
    modifiedOn?: string;
}

interface MasterDataItem {
    id: number;
    masterType: string;
    fieldName: string;
    displayName: string;
    isAggregator: boolean;
    aggregatorFunction: string;
    dataType: string;
    sortOrder: number;
}

interface PaymentTypeOption {
    id: string;
    name: string;
    value: string;
}

const ConfigureReport = () => {
    const navigate = useNavigate();
    const { reportId } = useParams<{ reportId: string }>();
    const [searchParams] = useSearchParams();
    const { toast } = useToast();
    const { t } = useTranslation();
    usePageTitle(t('shellAviationAIMS') + " | Configure Report");

    // Debug logging - check all params
    const cloneId = searchParams.get('clone');
    console.log('=== ConfigureReport Debug ===');
    console.log('reportId from params:', reportId);
    console.log('searchParams toString:', searchParams.toString());
    console.log('cloneId from searchParams:', cloneId);
    console.log('reportId === "new":', reportId === 'new');
    console.log('!!cloneId:', !!cloneId);
    
    const isEditMode = reportId && reportId !== 'new';
    const isCloneMode = (!reportId || reportId === 'new') && !!cloneId;
    
    console.log('isEditMode:', isEditMode);
    console.log('isCloneMode:', isCloneMode);
    console.log('=== End Debug ===');

    const [formData, setFormData] = useState<ReportConfig>({
        reportName: '',
        reportDescription: '',
        filterCriteria: 'Country',
        filterCriteriaValue: [],
        selectedGroups: [],
        selectedColumns: [],
        isActive: true
    });

    const [originalReportName, setOriginalReportName] = useState<string>('');
    const [auditInfo, setAuditInfo] = useState<AuditInfo>({});

    const [countries, setCountries] = useState<{id: string, name: string}[]>([]);
    const [airports, setAirports] = useState<{id: string, name: string, countryCode: string}[]>([]);
    const [customers, setCustomers] = useState<{id: string, name: string}[]>([]);
    const [paymentTypes, setPaymentTypes] = useState<PaymentTypeOption[]>([]);
    const [isLoadingFilters, setIsLoadingFilters] = useState(false);

    // Master data from API
    const [filterOptions, setFilterOptions] = useState<MasterDataItem[]>([]);
    const [groupOptions, setGroupOptions] = useState<MasterDataItem[]>([]);
    const [columnOptions, setColumnOptions] = useState<MasterDataItem[]>([]);
    const [isLoadingMasterData, setIsLoadingMasterData] = useState(false);

    const [isLoadingReport, setIsLoadingReport] = useState(false);

    const parseListField = (value?: string | null): string[] => {
        if (!value) return [];
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return parsed
                    .map((entry) => (typeof entry === 'string' ? entry.trim() : String(entry)))
                    .filter((entry) => entry.length > 0);
            }
        } catch (_) {
            // Fall through to comma split
        }
        return value
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => entry.length > 0);
    };

    const isFilterValueMandatory = useMemo(() => {
        const selectedFilter = filterOptions.find((option) => option.displayName === formData.filterCriteria);
        if (!selectedFilter) return true;
        const fieldName = (selectedFilter.fieldName || '').toLowerCase();
        const displayName = (selectedFilter.displayName || '').toLowerCase();
        return !(fieldName.includes('international') || fieldName.includes('intl') || displayName.includes('international'));
    }, [formData.filterCriteria, filterOptions]);

    const formatDateOnly = (value?: string) => {
        if (!value) return '-';
        const datePart = value.split('T')[0].split(' ')[0];
        return datePart || '-';
    };

    // Load master data from API
    useEffect(() => {
        setIsLoadingMasterData(true);
        setIsLoadingFilters(true);
        Promise.all([
            getData(ApiUrls.reportMasterData),
            getData(ApiUrls.countries),
            getData(ApiUrls.airports),
            getData(ApiUrls.customers),
            getData(ApiUrls.paymentTypes)
        ])
        .then(([masterDataResponse, countriesResponse, airportsResponse, customersResponse, paymentTypesResponse]) => {
            // Parse master data by type - access the nested data array
            const masterData = masterDataResponse.data.data;
            
            console.log('Master data received:', masterData);
            
            const filters = masterData.filter((item: MasterDataItem) => item.masterType === 'F');
            const groups = masterData.filter((item: MasterDataItem) => item.masterType === 'G');
            const columns = masterData.filter((item: MasterDataItem) => item.masterType === 'C');
            
            console.log('Filters:', filters);
            console.log('Groups:', groups);
            console.log('Columns:', columns);
            
            setFilterOptions(filters);
            setGroupOptions(groups);
            setColumnOptions(columns);
            const countriesData = countriesResponse.data?.data ?? countriesResponse.data ?? [];
            const airportsData = airportsResponse.data?.data ?? airportsResponse.data ?? [];
            const customersData = customersResponse.data?.data ?? customersResponse.data ?? [];
            const paymentTypesData = paymentTypesResponse.data?.data ?? paymentTypesResponse.data ?? [];
            setCountries(countriesData);
            setAirports(airportsData);
            setCustomers(customersData);
            setPaymentTypes(paymentTypesData.map((item: { paymentTypeCd: string; paymentType: string }) => ({
                id: item.paymentTypeCd,
                name: item.paymentType,
                value: item.paymentTypeCd
            })));
            setIsLoadingMasterData(false);
            setIsLoadingFilters(false);
        })
        .catch((error) => {
            console.error('Failed to load master data:', error);
            toast.error('Failed to load master data');
            setIsLoadingMasterData(false);
            setIsLoadingFilters(false);
        });
    }, []);

    const mapApiReportToForm = (apiReport: {
        reportName: string;
        reportDescription: string;
        filterId: number;
        filterCriteriaValue: string | null;
        selectedGroupId: number;
        selectedColumns: { id: number; columnName: string; displayOrder: number }[];
        isActive: boolean;
        isStatic?: boolean | number | string | null;
        createdBy?: string;
        createdOn?: string;
        modifiedBy?: string;
        modifiedOn?: string;
    }): ReportConfig => {
        const filter = filterOptions.find((option) => option.id === apiReport.filterId);
        const group = groupOptions.find((option) => option.id === apiReport.selectedGroupId);

        const selectedColumns = (apiReport.selectedColumns || [])
            .slice()
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((column) => {
                const match = columnOptions.find(
                    (option) =>
                        option.displayName === column.columnName || option.fieldName === column.columnName
                );
                return match ? match.displayName : column.columnName;
            });

        return {
            reportName: apiReport.reportName || '',
            reportDescription: apiReport.reportDescription || '',
            filterCriteria: filter ? filter.displayName : '',
            filterCriteriaValue: parseListField(apiReport.filterCriteriaValue),
            selectedGroups: group ? [group.displayName] : [],
            selectedColumns,
            isActive: apiReport.isActive
        };
    };

    const fetchReportById = (id: string, isClone: boolean) => {
        setIsLoadingReport(true);
        getData(`${ApiUrls.report}/${id}`)
            .then((response) => {
                const apiReport = response.data.data;
                const isStaticReport = apiReport?.isStatic === true || apiReport?.isStatic === 1 || apiReport?.isStatic === '1';
                if (isStaticReport) {
                    toast.error('Edit/Clone is not allowed for this report');
                    setIsLoadingReport(false);
                    navigate('/report/configure');
                    return;
                }
                const mapped = mapApiReportToForm(apiReport);
                setAuditInfo({
                    createdBy: apiReport.createdBy,
                    createdOn: apiReport.createdOn,
                    modifiedBy: apiReport.modifiedBy,
                    modifiedOn: apiReport.modifiedOn
                });
                if (isClone) {
                    setFormData({ ...mapped, reportName: '' });
                    setOriginalReportName(mapped.reportName);
                } else {
                    setFormData(mapped);
                }
                setIsLoadingReport(false);
            })
            .catch((error) => {
                console.error('Failed to load report:', error);
                toast.error('Failed to load report');
                setIsLoadingReport(false);
                navigate('/report/configure');
            });
    };

    // Load report data in edit mode (after master data is ready)
    useEffect(() => {
        if (isEditMode && reportId && filterOptions.length > 0) {
            fetchReportById(reportId, false);
        }
    }, [reportId, isEditMode, filterOptions.length]);

    // Load report data in clone mode (after master data is ready)
    useEffect(() => {
        if (isCloneMode && cloneId && filterOptions.length > 0) {
            fetchReportById(cloneId, true);
        }
    }, [isCloneMode, cloneId, filterOptions.length]);

    // Get filter criteria options from master data
    const filterCriteriaOptions = filterOptions.map(f => f.displayName);
    
    // Get options based on filter criteria
    const getFilterCriteriaValueOptions = () => {
        const selectedFilter = filterOptions.find(f => f.displayName === formData.filterCriteria);
        if (!selectedFilter) return [];

        const fieldName = (selectedFilter.fieldName || '').toLowerCase();
        const displayName = (selectedFilter.displayName || '').toLowerCase();

        if (fieldName.includes('country') || displayName.includes('country')) {
            return countries.map(c => ({ id: c.id, name: c.name, value: c.name }));
        }
        if (fieldName.includes('site') || fieldName.includes('airport') || displayName.includes('site') || displayName.includes('airport')) {
            return airports.map(a => ({ id: a.id, name: a.name, value: a.name }));
        }
        if (fieldName.includes('customer') || displayName.includes('customer')) {
            return customers.map(c => ({ id: c.id, name: c.name, value: c.id }));
        }
        if (fieldName.includes('payment') || displayName.includes('payment')) {
            if (paymentTypes.length > 0) {
                return paymentTypes;
            }
            return [
                { id: 'CA', name: 'Cash', value: 'CA' },
                { id: 'CC', name: 'Credit Card', value: 'CC' },
                { id: 'CN', name: 'Carnet', value: 'CN' },
                { id: 'CO', name: 'Contract', value: 'CO' }
            ];
        }
        if (fieldName.includes('international') || fieldName.includes('intl') || displayName.includes('international')) {
            return [
                { id: 'international', name: 'International', value: '1' },
                { id: 'domestic', name: 'Domestic', value: '0' },
                { id: 'empty', name: '<Empty>', value: null }
            ];
        }

        return [];
    };
    
    // Available groups from master data
    const availableGroups = groupOptions.map(g => g.displayName);
    
    // Available columns from master data
    const availableColumns = columnOptions.map(c => c.displayName);

    const validateForm = () => {
        if (!formData.reportName || formData.reportName.trim() === '') {
            toast.error('Report name is required');
            return false;
        }
        if (isCloneMode && formData.reportName === originalReportName) {
            toast.error('Please change the report name when cloning');
            return false;
        }
        if (!formData.filterCriteria || formData.filterCriteria.trim() === '') {
            toast.error('Filter criteria is required');
            return false;
        }
        const selectedFilter = filterOptions.find(f => f.displayName === formData.filterCriteria);
        if (!selectedFilter) {
            toast.error('Please select a valid filter criteria');
            return false;
        }
        if (isFilterValueMandatory && formData.filterCriteriaValue.length === 0) {
            toast.error('Filter criteria value is required');
            return false;
        }
        if (!formData.selectedColumns || formData.selectedColumns.length === 0) {
            toast.error('At least one column must be selected');
            return false;
        }
        return true;
    };

    const getSelectedFilter = () =>
        filterOptions.find((option) => option.displayName === formData.filterCriteria);

    const getSelectedGroup = () =>
        groupOptions.find((option) => option.displayName === formData.selectedGroups[0]);

    const getSelectedColumnsPayload = () =>
        formData.selectedColumns
            .map((columnName, index) => {
                const match = columnOptions.find((option) => option.displayName === columnName);
                if (!match) return null;
                return {
                    masterId: match.id,
                    displayOrder: index + 1
                };
            })
            .filter(Boolean) as { masterId: number; displayOrder: number }[];

    const handleSaveReport = () => {
        if (!validateForm()) {
            return;
        }

        const selectedFilter = getSelectedFilter();
        if (!selectedFilter) {
            toast.error('Please select a valid filter criteria');
            return;
        }

        const selectedGroup = getSelectedGroup();
        const selectedColumns = getSelectedColumnsPayload();

        if (selectedColumns.length === 0) {
            toast.error('Please select at least one valid column');
            return;
        }

        const payload = {
            reportName: formData.reportName,
            reportDescription: formData.reportDescription,
            filterId: selectedFilter.id,
            filterCriteriaValue: formData.filterCriteriaValue.length > 0 ? formData.filterCriteriaValue.join(',') : null,
            selectedGroupId: selectedGroup ? selectedGroup.id : 0,
            selectedColumns,
            isActive: formData.isActive
        };

        const request = isEditMode && reportId
            ? putData(`${ApiUrls.report}/${reportId}`, payload)
            : postData(ApiUrls.report, payload);

        request
            .then(() => {
                toast.success('Report saved successfully');
                navigate('/report/configure');
            })
            .catch((error) => {
                console.error('Failed to save report:', error);
                toast.error('Failed to save report');
            });
    };

    const handleCancel = () => {
        navigate('/report/configure');
    };

    return (
        <ContainerDiv className="report-container">
            <Card className="report-card" style={{border: '2px solid #d9d9d9', borderRadius: '8px', padding: '24px'}}>
                {(isLoadingMasterData || isLoadingReport) && <Loader />}
                {!(isLoadingMasterData || isLoadingReport) && (
                    <>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px'}}>
                            <Heading level={2} className="report-heading" style={{margin: 0, fontWeight: 700}}>
                                {isEditMode ? 'Edit Report' : isCloneMode ? 'Clone Report' : 'Add New Report'}
                            </Heading>
                            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                <Label bold>Is Active</Label>
                                <Toggle 
                                    checked={formData.isActive} 
                                    onChange={(value) => setFormData({...formData, isActive: value})} 
                                />
                            </div>
                        </div>
                        
                        <Grid columns={2} gap='4%'>
                    <div>
                        <Grid columns="30% 70%" style={{width:"95%"}}>
                            
                            <Col size="100"><Label bold type={TextTypes.Label} htmlFor='reportName'>Report Name <span style={{ color: 'red' }}>*</span></Label></Col>
                            <Col size="100">
                                <TextInput
                                    id='reportName'
                                    value={formData.reportName}
                                    onChange={(e) => setFormData({...formData, reportName: e.target.value})}
                                    maxLength={100}
                                    placeholder="Enter report name"
                                />
                            </Col>

                            <Col size="100"><Label bold type={TextTypes.Label} htmlFor='reportDescription'>Report Description</Label></Col>
                            <Col size="100">
                                <TextArea
                                    id='reportDescription'
                                    value={formData.reportDescription}
                                    onChange={(e) => setFormData({...formData, reportDescription: e.target.value})}
                                    maxLength={250}
                                    rows={4}
                                    placeholder="Enter report description"
                                />
                            </Col>

                        </Grid>
                    </div>

                    <div>
                        <Grid columns="30% 70%" style={{width:"95%"}}>
                            
                            <Col size="100"><Label bold type={TextTypes.Label} htmlFor='group'>Group</Label></Col>
                            <Col size="100">
                                <Select 
                                    id='group'
                                    placeholder="Select group"
                                    value={formData.selectedGroups[0]}
                                    size="medium"
                                    showSearch 
                                    style={{ width: '100%' }}
                                    onChange={(value) => setFormData({...formData, selectedGroups: [value]})}
                                >
                                    {availableGroups.map((group) => (
                                        <Option key={group} value={group}>
                                            {group}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>

                            <Col size="100"><Label bold type={TextTypes.Label} htmlFor='filterCriteria'>Filter Criteria <span style={{ color: 'red' }}>*</span></Label></Col>
                            <Col size="100">
                                <Select
                                    id='filterCriteria'
                                    value={formData.filterCriteria}
                                    onChange={(value) => setFormData({...formData, filterCriteria: value, filterCriteriaValue: []})}
                                    placeholder="Select filter criteria"
                                >
                                    {filterCriteriaOptions.map((option) => (
                                        <Option key={option} value={option}>
                                            {option}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>

                            {formData.filterCriteria && (
                                <>
                                    <Col size="100">
                                        <Label bold type={TextTypes.Label} htmlFor='filterCriteriaValue'>
                                            Filter Value {isFilterValueMandatory && <span style={{ color: 'red' }}>*</span>}
                                        </Label>
                                    </Col>
                                    <Col size="100">
                                        <Select
                                            id='filterCriteriaValue'
                                            mode="multiple"
                                            value={formData.filterCriteriaValue}
                                            onChange={(value) => setFormData({...formData, filterCriteriaValue: Array.isArray(value) ? (value as string[]) : []})}
                                            placeholder={`Select ${formData.filterCriteria.toLowerCase()}`}
                                            showSearch
                                            loading={isLoadingFilters}
                                            style={{ width: '100%' }}
                                        >
                                            {getFilterCriteriaValueOptions().map((option) => (
                                                <Option key={option.id} value={option.value ?? option.name}>
                                                    {option.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Col>
                                </>
                            )}

                        </Grid>
                    </div>
                </Grid>

                <div style={{marginTop:'30px', marginBottom:'20px'}}>
                    <hr style={{border: '0', borderTop: '1px solid #d9d9d9', margin: '0'}}/>
                </div>

                <div style={{marginBottom:'20px'}}>
                    <Heading level={3} style={{margin: 0, fontSize: '18px', fontWeight: 600}}>
                        Choose Report Columns
                    </Heading>
                </div>

                <div style={{marginTop:'20px'}}>
                    <Grid columns="15% 85%" style={{width:"97.5%"}}>
                        <Col size="100">
                            <div style={{paddingTop: '8px'}}>
                                <Label bold type={TextTypes.Label} htmlFor='columns'>Columns <span style={{ color: 'red' }}>*</span></Label>
                            </div>
                        </Col>
                        <Col size="100">
                            <TransferList
                                availableItems={availableColumns}
                                selectedItems={formData.selectedColumns}
                                onItemsChange={(selected) => setFormData({...formData, selectedColumns: selected})}
                                leftTitle="Available Columns"
                                rightTitle="Selected Columns"
                            />
                        </Col>
                    </Grid>
                </div>

                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop:'40px'}}>
                            {isEditMode ? (
                                <div style={{fontSize: '14px', color: '#6c757d'}}>
                                    <span>
                                        <strong>Modified By:</strong> {auditInfo.modifiedBy || auditInfo.createdBy || '-'}
                                    </span>
                                    <span style={{marginLeft: '16px'}}>
                                        <strong>Modified On:</strong> {formatDateOnly(auditInfo.modifiedOn || auditInfo.createdOn)}
                                    </span>
                                </div>
                            ) : (
                                <div />
                            )}
                            <div>
                            <Button 
                                variant={Variants.Outlined}
                                onClick={handleCancel}
                                style={{marginLeft:'5px'}}
                            >
                                CANCEL
                            </Button>
                            <Button 
                                variant={Variants.Filled}
                                onClick={handleSaveReport}
                                style={{marginLeft:'5px'}}
                                disabled={
                                    !formData.reportName || 
                                    formData.reportName.trim() === '' ||
                                    !formData.filterCriteria || 
                                    formData.filterCriteria.trim() === '' ||
                                    (isFilterValueMandatory && formData.filterCriteriaValue.length === 0) ||
                                    !formData.selectedColumns || 
                                    formData.selectedColumns.length === 0
                                }
                            >
                                SAVE REPORT
                            </Button>
                            </div>
                        </div>
                    </>
                )}
            </Card>
        </ContainerDiv>
    );
};

export default ConfigureReport;